










































































































































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import { mixins } from "vue-class-component";

import VuePlotly from "@statnett/vue-plotly";

import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import { fmtFloat, UNIT_SUFFIX } from "./formatters";
import { buildRcaUrl, RcaFilters } from "./filterQueryParams";
import HelpIcon from "./HelpIcon.vue";
import PlotlyResizeMixin from "./plotly-resize-mixin";

interface PanelState {
    loading: boolean;
    error: string | null;
    data: any[];
}
function newPanelState(): PanelState {
    return { loading: false, error: null, data: [] };
}

interface MetricOption {
    key: string;
    label: string;
    valueformat: string;
    unit: string;
}

const METRIC_OPTIONS: MetricOption[] = [
    {
        key: "impacted_exposed_tonnes",
        label: "Impacted tonnes",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.tonnes,
    },
    {
        key: "rerouted_exposed_tonnes",
        label: "Rerouted tonnes",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.tonnes,
    },
    {
        key: "blocked_exposed_tonnes",
        label: "Blocked tonnes",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.tonnes,
    },
    {
        key: "baseline_exposed_tonnes",
        label: "Baseline tonnes",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.tonnes,
    },
    {
        key: "impacted_exposed_trip_count",
        label: "Impacted supply chain legs (trips)",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.none,
    },
    {
        key: "rerouted_exposed_trip_count",
        label: "Rerouted supply chain legs (trips)",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.none,
    },
    {
        key: "blocked_exposed_trip_count",
        label: "Blocked supply chain legs (trips)",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.none,
    },
    {
        key: "baseline_exposed_trip_count",
        label: "Baseline supply chain legs (trips)",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.none,
    },
];

const GROUP_LEVELS = ["sector", "industry", "commodity"] as const;
type GroupLevel = (typeof GROUP_LEVELS)[number];

/**
 * Sector colour palette matching the SCBD benchmarking sunburst.
 *
 * Keys use normalised sector names (e.g. `"Wood Product"` → `"wood_product"`).
 *
 * Industries and commodities inherit their parent sector's colour, so colours
 * remain consistent across all hierarchy levels.
 */
const SUNBURST_SECTOR_COLORS: Record<string, string> = {
    beverage: "#000066",
    chemicals: "#4f205b",
    cold_food: "#2918EC",
    construction: "#c2b280",
    cropping: "#bcbd22",
    food: "#1f77b4",
    fuel: "#7f7f7f",
    general: "#9467bd",
    health: "#fcc0a5",
    horticulture: "#2ca02c",
    livestock: "#ff7f0e",
    minerals: "#e377c2",
    ores_coal: "#bb45a2",
    vehicles: "#d62728",
    waste: "#17becf",
    wood_product: "#8c564b",
};
/** Used for any sector that doesn't appear in SUNBURST_SECTOR_COLORS — keeps the
 * dashboard's navy as the unmapped fallback rather than black, so unrecognised
 * sectors still feel on-brand. */
const SECTOR_COLOR_FALLBACK = "#013e75";

function normalizeSectorKey(value: any): string {
    return String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");
}

function sectorColor(value: any): string {
    return (
        SUNBURST_SECTOR_COLORS[normalizeSectorKey(value)] ??
        SECTOR_COLOR_FALLBACK
    );
}

@Component({ components: { VuePlotly, HelpIcon } })
export default class SectorTab extends mixins(PlotlyResizeMixin) {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;
    /** Global sidebar filters — narrow freight hierarchy rollups to matching trips.
     * We always pass ``sector=null industry=null`` to the endpoint and let the global
     * filter narrow the rows. */
    @Prop({ default: () => ({}) }) filters!: RcaFilters;
    /** Whether this tab is currently active. Plotly charts that rendered while the tab was
     * hidden appear to be at collapsed width; we re-render them when the tab becomes visible. */
    @Prop({ default: true }) active!: boolean;

    fmtFloat = fmtFloat;
    metricOptions = METRIC_OPTIONS;
    groupLevelOptions = [
        { text: "Sector", value: "sector" },
        { text: "Industry", value: "industry" },
        { text: "Commodity", value: "commodity" },
    ];

    groupLevel: GroupLevel = "sector";
    metric: string = METRIC_OPTIONS[0].key;

    /** Local display cap for this tab's two charts AND the table. Client-side: the rollup loads
     * all groups; only the daily heatmap fetch is bounded to the top 50 by the selected metric. */
    sectorTopN = 15;

    /** Help text for the "Show top" slider's ? icon. */
    sliderHelp =
        "Sets how many of the top-ranked groups to display in the charts and table below, ranked by the selected metric. Note: TraNSIT contains more than 50 commodities, but only the top 50 are shown here.";

    rollup: PanelState = newPanelState();
    daily: PanelState = newPanelState();

    mounted() {
        this.fetchRollup();
        this.fetchDaily();
    }

    /** One key over every fetch input so a param change triggers exactly one fetch. */
    private get fetchParamsKey(): string {
        return `${this.startDate}|${this.endDate}|${
            this.groupLevel
        }|${JSON.stringify(this.filters)}`;
    }

    @Watch("fetchParamsKey")
    onParamsChanged() {
        this.fetchRollup();
        this.fetchDaily();
    }

    /** The heatmap's member groups follow the selected metric, so refetch just the daily series
     * when the metric changes. The bar chart + table re-rank client-side, so they need no fetch. */
    @Watch("metric")
    onMetricChanged() {
        this.fetchDaily();
    }

    resizeCharts() {
        this.refitPlotly(["barChart", "heatmapChart"]);
    }

    get currentMetric(): MetricOption {
        return (
            METRIC_OPTIONS.find((m) => m.key === this.metric) ??
            METRIC_OPTIONS[0]
        );
    }

    get rollupRows(): any[] {
        return this.rollup.data;
    }

    get rollupTableRows(): any[] {
        return this.rankedRollup.slice(0, this.sectorTopN);
    }

    /** Bar chart + detail table: load EVERY group (no limit). They sort by the selected metric
     * and slice to sectorTopN client-side. */
    fetchRollup() {
        if (!this.startDate || !this.endDate) return;
        this.fetchPanel(
            this.rollup,
            endpoints.roadClosureAnalyticsFreightHierarchyRollupUrl,
            {
                start_date: this.startDate,
                end_date: this.endDate,
                group_level: this.groupLevel,
            },
        );
    }

    /** Heatmap: bounded fetch of the top 50 groups (the slider's max) ranked by the current
     * metric — group × day data is heavy, so we never load all groups. The slider then slices
     * these 50 client-side (heatmapPivot), so moving it does NOT refetch; ONLY a metric (or
     * date / group / filter) change refetches, via @Watch("metric"). The 50 matches the slider :max. */
    fetchDaily() {
        if (!this.startDate || !this.endDate) return;
        this.fetchPanel(
            this.daily,
            endpoints.roadClosureAnalyticsFreightHierarchyDailyUrl,
            {
                start_date: this.startDate,
                end_date: this.endDate,
                group_level: this.groupLevel,
                limit: 50,
                metric: this.metric,
            },
        );
    }

    fetchPanel(panel: PanelState, url: string, params: Record<string, any>) {
        panel.loading = true;
        panel.error = null;
        eventLoading.startLoading();
        Vue.axios({
            url: buildRcaUrl(url, params, this.filters),
        })
            .then((response: any) => {
                panel.data = response.data || [];
            })
            .catch((err: any) => {
                console.error("[RCA Sector]", err);
                panel.error = err?.message || "Failed to load.";
                panel.data = [];
            })
            .finally(() => {
                panel.loading = false;
                eventLoading.stopLoading();
            });
    }

    /**
     * Bar chart sorted by the selected metric (descending) so the heaviest groups are leftmost.
     */
    get rankedRollup(): any[] {
        return [...this.rollupRows].sort(
            (a, b) =>
                (Number(b[this.metric]) || 0) - (Number(a[this.metric]) || 0),
        );
    }

    get barTraces(): any[] {
        const rows = this.rankedRollup.slice(0, this.sectorTopN);
        return [
            {
                type: "bar",
                x: rows.map((r) => r.hierarchy_value),
                y: rows.map((r) => Number(r[this.metric]) || 0),
                // Per-bar colour matched to the SCE benchmarking sunburst's sector
                // palette. Each row carries its parent ``sector`` (commod_l3) regardless
                // of the chosen group level, so industries / commodities stay visually
                // grouped under their sector colour.
                marker: { color: rows.map((r) => sectorColor(r.sector)) },
                hovertemplate:
                    "%{x}<br>" +
                    this.currentMetric.label +
                    ": %{y:" +
                    this.currentMetric.valueformat +
                    "}" +
                    this.currentMetric.unit +
                    "<extra></extra>",
            },
        ];
    }

    get barLayout(): any {
        return {
            dragmode: false,
            height: 380,
            margin: { l: 60, r: 16, t: 8, b: 130 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            xaxis: { automargin: true, tickangle: -35 },
            yaxis: {
                automargin: true,
                title: this.currentMetric.label,
                gridcolor: "rgba(0,0,0,0.06)",
                exponentformat: "SI",
                showexponent: "all",
                nticks: 6,
                rangemode: "tozero",
            },
            showlegend: false,
        };
    }

    /**
     * Pivot daily rows into a hierarchy_value × scenario_date_local matrix of the selected
     * metric. Same adaptive-tick approach as the LGA heatmap so date labels stay readable as the
     * range grows.
     */
    get heatmapPivot(): {
        z: number[][];
        x: string[];
        y: string[];
    } | null {
        const rows = Array.isArray(this.daily.data) ? this.daily.data : [];
        if (!rows.length) return null;

        const dateSet = new Set<string>();
        const groupTotals = new Map<string, number>();
        const cells = new Map<string, number>();

        for (const r of rows) {
            const group = r.hierarchy_value;
            if (!group) continue;
            const date = String(r.scenario_date_local).slice(0, 10);
            const v = Number(r[this.metric]) || 0;
            dateSet.add(date);
            groupTotals.set(group, (groupTotals.get(group) || 0) + v);
            cells.set(`${group}__${date}`, v);
        }

        const dates = Array.from(dateSet).sort();
        const groups = Array.from(groupTotals.keys())
            .sort(
                (a, b) => (groupTotals.get(b) || 0) - (groupTotals.get(a) || 0),
            )
            .slice(0, this.sectorTopN);
        const z = groups.map((g) =>
            dates.map((d) => cells.get(`${g}__${d}`) || 0),
        );
        return { z, x: dates, y: groups };
    }

    get heatmapTraces(): any[] {
        const p = this.heatmapPivot;
        if (!p) return [];
        return [
            {
                type: "heatmap",
                z: p.z,
                x: p.x,
                y: p.y,
                colorscale: "YlOrRd",
                reversescale: true,
                colorbar: {
                    title: { text: this.currentMetric.label, side: "right" },
                    thickness: 12,
                },
                hovertemplate:
                    "Group: %{y}<br>Date: %{x}<br>" +
                    this.currentMetric.label +
                    ": %{z:" +
                    this.currentMetric.valueformat +
                    "}" +
                    this.currentMetric.unit +
                    "<extra></extra>",
            },
        ];
    }

    get heatmapLayout(): any {
        const p = this.heatmapPivot;
        const rowCount = p ? p.y.length : 1;
        const dates = p ? p.x : [];

        // Adaptive label thinning — same approach as the LGA heatmap.
        const targetLabels = 25;
        const step = Math.max(1, Math.ceil(dates.length / targetLabels));
        const tickvals = dates.filter((_d, i) => i % step === 0);
        const formatTick = (d: string) => {
            const sample = dates[0] || "";
            const lastSample = dates[dates.length - 1] || "";
            const multiYear = sample.slice(0, 4) !== lastSample.slice(0, 4);
            const dt = new Date(d + "T00:00:00");
            if (Number.isNaN(dt.getTime())) return d;
            return dt.toLocaleDateString("en-AU", {
                day: "2-digit",
                month: "short",
                year: multiYear ? "numeric" : undefined,
            });
        };
        const ticktext = tickvals.map(formatTick);

        return {
            dragmode: false,
            height: Math.max(360, 28 * rowCount + 80),
            margin: { l: 8, r: 60, t: 8, b: 100 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            xaxis: {
                type: "category",
                automargin: true,
                tickangle: -45,
                tickmode: "array",
                tickvals,
                ticktext,
                title: { text: "Scenario date", standoff: 24 },
            },
            yaxis: {
                automargin: true,
                autorange: "reversed",
                ticksuffix: "  ",
            },
        };
    }

    /**
     * Build the table headers dynamically — the hierarchy columns shown depend on the level so
     * users see the right ancestor context (e.g. sector + industry on commodity rows).
     */
    get tableHeaders(): any[] {
        const headers: any[] = [{ text: "Group", value: "hierarchy_value" }];
        if (this.groupLevel === "industry") {
            headers.push({ text: "Sector", value: "sector" });
        }
        if (this.groupLevel === "commodity") {
            headers.push({ text: "Sector", value: "sector" });
            headers.push({ text: "Industry", value: "industry" });
        }
        headers.push(
            {
                text: "Baseline trips",
                value: "baseline_exposed_trip_count",
                align: "end",
            },
            {
                text: "Baseline tonnes",
                value: "baseline_exposed_tonnes",
                align: "end",
            },
            {
                text: "Impacted trips",
                value: "impacted_exposed_trip_count",
                align: "end",
            },
            {
                text: "Impacted tonnes",
                value: "impacted_exposed_tonnes",
                align: "end",
            },
            {
                text: "Rerouted trips",
                value: "rerouted_exposed_trip_count",
                align: "end",
            },
            {
                text: "Rerouted tonnes",
                value: "rerouted_exposed_tonnes",
                align: "end",
            },
            {
                text: "Blocked trips",
                value: "blocked_exposed_trip_count",
                align: "end",
            },
            {
                text: "Blocked tonnes",
                value: "blocked_exposed_tonnes",
                align: "end",
            },
        );
        return headers;
    }
}
